import { useContext, useState, useEffect } from "react";
import { LocaleContext } from "../../contexts/LocaleContext";
import { Link } from "react-router-dom";
import Pagination from "../../components/Pagination";
import { blockUserService } from "../../services/blockUserService";
import { cityService } from "../../services/cityService";
import Loading from "../../components/common/Loading";
import { toast } from "../../services/toastService";
import { params } from "../../config/params";
import { searchParams } from "../../config/searchParams";

const emptyFilters = {
  isBlocked: "",
  mobileFilter: "",
  cityId: "",
  dateFrom: "",
  dateTo: "",
};

const BlockUsers = () => {
  const { t, lang } = useContext(LocaleContext);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [cities, setCities] = useState([]);
  const [filters, setFilters] = useState(emptyFilters);
  const [showFilters, setShowFilters] = useState(false);
  const [loadingId, setLoadingId] = useState(null);

  useEffect(() => {
    setLoading(true);
    getAllAsync();
    loadCities();
  }, []);

  const loadCities = async () => {
    try {
      const result = await cityService.getDropdownAsync();
      setCities(result || []);
    } catch (err) {
      console.error(err);
    }
  };

  const buildParams = (extraFilters = filters) => {
    const merged = { ...params };
    if (extraFilters.isBlocked !== "") merged.isBlocked = extraFilters.isBlocked === "true";
    if (extraFilters.mobileFilter) merged.mobileFilter = extraFilters.mobileFilter;
    if (extraFilters.cityId) merged.cityId = extraFilters.cityId;
    if (extraFilters.dateFrom) merged.dateFrom = extraFilters.dateFrom;
    if (extraFilters.dateTo) merged.dateTo = extraFilters.dateTo;
    return merged;
  };

  const getAllAsync = async (extraFilters = filters) => {
    try {
      const result = await blockUserService.getAllAsync(buildParams(extraFilters));
      setData(result);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getAllSearchAsync = async (signal) => {
    try {
      const result = await blockUserService.getAllSearchAsync(
        { ...searchParams, ...buildParams() },
        signal
      );
      setData(result);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (search.trim().length < 2) {
      params.pageNumber = 1;
      getAllAsync();
      return;
    }
    const controller = new AbortController();
    const delay = setTimeout(() => {
      searchParams.search = search.trim();
      searchParams.pageNumber = 1;
      getAllSearchAsync(controller.signal);
    }, 400);
    return () => {
      controller.abort();
      clearTimeout(delay);
    };
  }, [search]);

  const onPageChange = (pageNumber) => {
    setLoading(true);
    if (!search) {
      params.pageNumber = pageNumber;
      getAllAsync();
      return;
    }
    searchParams.pageNumber = pageNumber;
    getAllSearchAsync();
  };

  const applyFilters = () => {
    params.pageNumber = 1;
    setLoading(true);
    setShowFilters(false);
    getAllAsync(filters);
  };

  const clearFilters = () => {
    setFilters(emptyFilters);
    params.pageNumber = 1;
    setLoading(true);
    getAllAsync(emptyFilters);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleToggle = async (id, currentIsBlocked) => {
    setLoadingId(id);
    setData((prev) => ({
      ...prev,
      items: prev.items.map((item) =>
        item.id === id ? { ...item, isBlocked: !item.isBlocked } : item
      ),
    }));
    try {
      const response = await blockUserService.toggle(id);
      if (!response.isSuccess) {
        setData((prev) => ({
          ...prev,
          items: prev.items.map((item) =>
            item.id === id ? { ...item, isBlocked: currentIsBlocked } : item
          ),
        }));
        toast.error(t("anErrorOccurredPleaseTryAgainLater"));
      }
    } catch {
      setData((prev) => ({
        ...prev,
        items: prev.items.map((item) =>
          item.id === id ? { ...item, isBlocked: currentIsBlocked } : item
        ),
      }));
      toast.error(t("anErrorOccurredPleaseTryAgainLater"));
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <>
      {loading && <Loading />}
      <section className="block-users">
        <div className="default-heading">{t("blockUsers")}</div>
        <section className="section-grid">
          <div className="grid-card">
            <div className="grid-card-header">
              <strong>{t("blockUsers")}</strong>
              <p>{t("theseAreYourLatestBlockedUsers")}</p>
            </div>
            <div className="grid-card-body">
              <div className="table-header">
                <div className="table-header-icons hidden">
                  <div className="icon-box"><i className="fas fa-file-pdf"></i></div>
                  <div className="icon-box"><i className="fas fa-file-excel"></i></div>
                  <div className="icon-box"><i className="fas fa-print"></i></div>
                </div>
                <div className="table-search">
                  <div className="input-icon input-icon-width">
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="form-control input-search"
                      placeholder={t("search")}
                    />
                    <i className="fas fa-search"></i>
                  </div>
                  <div className="grid-btn">
                    <Link to="/block-user-form">
                      <button className="btn-default my-bg-secondary">
                        <i className="fas fa-plus"></i> {t("addNewBlock")}
                      </button>
                    </Link>
                  </div>
                </div>
              </div>
              <div className="table-delete-option mt-3">
                <b>{t("allCustomers")}</b>
                <div className="d-flex align-items-center gap-2">
                  <i className="fas fa-filter cursor-pointer" onClick={() => setShowFilters((v) => !v)} />
                  <span></span>
                </div>
              </div>
              {showFilters && (
                <div className="p-3 border rounded mb-3 bg-white">
                  <div className="row g-2">
                    <div className="col-12 col-md-3">
                      <label className="form-label fw-bold">{t("status")}</label>
                      <select
                        className="form-control form-control-sm"
                        value={filters.isBlocked}
                        onChange={(e) => handleFilterChange("isBlocked", e.target.value)}
                      >
                        <option value="">{t("all")}</option>
                        <option value="true">{t("blocked")}</option>
                        <option value="false">{t("active")}</option>
                      </select>
                    </div>
                    <div className="col-12 col-md-3">
                      <label className="form-label fw-bold">{t("mobileNo")}</label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={filters.mobileFilter}
                        onChange={(e) => handleFilterChange("mobileFilter", e.target.value)}
                        placeholder={t("mobileNo")}
                      />
                    </div>
                    <div className="col-12 col-md-3">
                      <label className="form-label fw-bold">{t("city")}</label>
                      <select
                        className="form-control form-control-sm"
                        value={filters.cityId}
                        onChange={(e) => handleFilterChange("cityId", e.target.value)}
                      >
                        <option value="">{t("all")}</option>
                        {cities.map((city) => (
                          <option key={city.id} value={city.id}>{city.name?.[lang]}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-12 col-md-3">
                      <label className="form-label fw-bold">{t("date")}</label>
                      <input type="date" className="form-control form-control-sm mb-1" value={filters.dateFrom} onChange={(e) => handleFilterChange("dateFrom", e.target.value)} />
                      <input type="date" className="form-control form-control-sm" value={filters.dateTo} onChange={(e) => handleFilterChange("dateTo", e.target.value)} />
                    </div>
                    <div className="col-12 d-flex gap-2 justify-content-end">
                      <button className="btn-default" onClick={applyFilters}>{t("search")}</button>
                      <button className="btn-secondary" onClick={clearFilters}>{t("cancel")}</button>
                    </div>
                  </div>
                </div>
              )}
              <div className="my-table">
                <table className="table">
                  <thead>
                    <tr>
                      <th><input type="checkbox" /></th>
                      <th>{t("userName")}</th>
                      <th>{t("mobileNo")}</th>
                      <th>{t("email")}</th>
                      <th>{t("city")}</th>
                      <th>{t("status")}</th>
                      <th>{t("date")}</th>
                      <th>{t("actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.items?.map((item) => (
                      <tr key={item.id}>
                        <td><input type="checkbox" /></td>
                        <td>{item.fullName}</td>
                        <td>{item.mobileNumber}</td>
                        <td>{item.email}</td>
                        <td>{item.city?.[lang]}</td>
                        <td>
                          <span className={`badge ${item.isBlocked ? "bg-danger" : "bg-success"}`}>
                            {item.isBlocked ? t("blocked") : t("active")}
                          </span>
                        </td>
                        <td>{item.blockedAt?.toDate()}</td>
                        <td>
                          <div className="flex-center">
                            {loadingId === item.id ? (
                              <span className="spinner-border spinner-border-sm" />
                            ) : (
                              <div className="form-check form-switch" onClick={() => handleToggle(item.id, item.isBlocked)}>
                                <input
                                  className="form-check-input cursor-pointer"
                                  type="checkbox"
                                  checked={item.isBlocked}
                                  onChange={() => {}}
                                />
                                <label className="form-check-label">
                                  {item.isBlocked ? t("unblock") : t("block")}
                                </label>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {data?.items?.length === 0 && (
                      <tr>
                        <td colSpan="100%" className="text-center py-3">
                          {t("noResultsFound")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="grid-footer">
              {data && (
                <Pagination
                  t={t}
                  pageNumber={data.pageNumber}
                  totalPages={data.totalPages}
                  hasNextPage={data.hasNextPage}
                  hasPreviousPage={data.hasPreviousPage}
                  onPageChange={onPageChange}
                />
              )}
            </div>
          </div>
        </section>
      </section>
    </>
  );
};

export default BlockUsers;
